import React, { useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Star as StarIcon, Trash2 as TrashIcon, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Snap-scroll photo carousel used on the Result page — primary photo first,
 * then every `extra_images[]` slide. Renders dot indicators + a small
 * "n/total" pill at the bottom, plus prev/next arrow buttons at the left
 * and right edges so mouse users on desktop (who can't swipe) have a
 * discoverable, clickable way to move between photos. The trash and "set
 * as cover" affordances in the bottom-left appear ONLY when a non-primary
 * slide is currently active.
 *
 * The parent owns:
 *  - `carouselRef` (so it can scrollIntoView when adding photos)
 *  - `carouselIndex` state + `onScroll` handler (kept in parent so other
 *    controls can react to which slide is visible)
 *  - `onRemoveExtra(photo_id)` (deletes from scan.extra_images)
 *  - `onSetPrimary(photo_id)` (promotes the extra to be the scan's cover)
 *
 * We use a merged-ref pattern so the parent's forwarded ref AND our
 * internal ref (used for the prev/next scroll animation) both point at
 * the same element — no extra parent plumbing required.
 */
export const PhotoCarousel = React.forwardRef(function PhotoCarousel(
  { primaryPhoto, extraPhotos, altText, carouselIndex, onScroll, onSlideClick, onRemoveExtra, onSetPrimary },
  carouselRef,
) {
  const { t } = useTranslation();
  const internalRef = useRef(null);

  // Merge the parent's forwarded ref with our internal one so both point at
  // the scroll container. `useCallback` keeps the ref-setter identity stable
  // (otherwise React would re-run it on every render, briefly nulling refs).
  const setRefs = useCallback((el) => {
    internalRef.current = el;
    if (typeof carouselRef === "function") carouselRef(el);
    else if (carouselRef) carouselRef.current = el;
  }, [carouselRef]);

  const items = [
    { src: primaryPhoto, photo_id: "primary", isPrimary: true },
    ...(extraPhotos || []).map((p) => ({ src: p.image_base64, photo_id: p.photo_id, isPrimary: false })),
  ];
  const activeExtra = carouselIndex > 0 ? (extraPhotos || [])[carouselIndex - 1] : null;

  // Scroll by exactly one slide width. Each slide is `w-full` of the
  // container, so `clientWidth` is the correct step size regardless of
  // viewport. `scroll-behavior: smooth` on the container animates the jump.
  const scrollBySlide = (direction) => {
    const el = internalRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth, behavior: "smooth" });
  };

  const atStart = carouselIndex <= 0;
  const atEnd = carouselIndex >= items.length - 1;

  return (
    <>
      <div
        ref={setRefs}
        onScroll={onScroll}
        className="absolute inset-0 w-full h-full overflow-x-auto overflow-y-hidden snap-x snap-mandatory flex no-scrollbar"
        style={{ scrollBehavior: "smooth" }}
      >
        {items.map((item, i) => (
          <button
            key={item.photo_id}
            type="button"
            onClick={() => onSlideClick(item.src)}
            aria-label={t("result.viewFullPhotoAria")}
            data-testid={i === 0 ? "hero-photo-button" : `carousel-slide-${item.photo_id}`}
            className="relative shrink-0 snap-start w-full h-full focus:outline-none"
          >
            <img src={item.src} alt={altText} className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
      {/* Prev/next arrows for mouse users. Rendered above the slides via
          z-10 and vertically centered. Slightly translucent so they don't
          fight the photo; opaque on hover. Disabled at either end. */}
      {items.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => scrollBySlide(-1)}
            disabled={atStart}
            data-testid="carousel-prev"
            aria-label={t("result.carouselPrevAria")}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/45 backdrop-blur text-white flex items-center justify-center hover:bg-black/70 transition disabled:opacity-30 disabled:cursor-not-allowed z-10"
          >
            <ChevronLeft className="w-5 h-5" strokeWidth={2} />
          </button>
          <button
            type="button"
            onClick={() => scrollBySlide(1)}
            disabled={atEnd}
            data-testid="carousel-next"
            aria-label={t("result.carouselNextAria")}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/45 backdrop-blur text-white flex items-center justify-center hover:bg-black/70 transition disabled:opacity-30 disabled:cursor-not-allowed z-10"
          >
            <ChevronRight className="w-5 h-5" strokeWidth={2} />
          </button>
        </>
      )}
      {items.length > 1 && (
        <div
          className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5"
          data-testid="carousel-dots"
          aria-hidden="true"
        >
          {items.map((item, i) => (
            <span
              key={`dot-${item.photo_id}`}
              className={`h-1.5 rounded-full transition-all ${
                i === carouselIndex ? "w-5 bg-white" : "w-1.5 bg-white/55"
              }`}
            />
          ))}
        </div>
      )}
      {items.length > 1 && (
        <div
          className="absolute bottom-3 right-3 rounded-full bg-black/45 text-white text-[10px] px-2 py-1 backdrop-blur"
          data-testid="carousel-index"
        >
          {carouselIndex + 1}/{items.length}
        </div>
      )}
      {items.length > 1 && carouselIndex === 0 && (
        <div
          data-testid="carousel-cover-badge"
          aria-hidden="true"
          className="absolute bottom-3 left-3 rounded-full bg-black/45 text-white text-[10px] uppercase tracking-[0.14em] px-2.5 py-1 backdrop-blur flex items-center gap-1"
        >
          <StarIcon className="w-3 h-3" strokeWidth={1.75} fill="currentColor" />
          {t("result.coverBadge")}
        </div>
      )}
      {activeExtra && (
        <>
          {/* Set-as-cover pill in bottom-left — kept subtle so it doesn't
              overpower the photo. Only surfaces on non-primary slides. */}
          {onSetPrimary && (
            <button
              type="button"
              onClick={() => onSetPrimary(activeExtra.photo_id)}
              data-testid="carousel-set-primary"
              aria-label={t("result.setPrimaryAria")}
              title={t("result.setPrimary")}
              className="absolute bottom-3 left-3 h-7 px-2.5 rounded-full bg-white/90 backdrop-blur text-leaf-ink text-[10px] uppercase tracking-[0.14em] inline-flex items-center gap-1 hover:bg-white transition shadow-botanical z-10"
            >
              <StarIcon className="w-3 h-3" strokeWidth={1.75} />
              {t("result.setPrimary")}
            </button>
          )}
          {/* Delete-photo affordance lives in the OPPOSITE (top-right) corner
              intentionally, so a user's thumb can't hit it while trying to
              tap "Set as cover" — and it's very small + iconographic + gated
              behind a confirmation prompt so accidental taps don't destroy
              a photo silently. */}
          <button
            type="button"
            onClick={() => {
              if (window.confirm(t("result.photoDeleteConfirm"))) {
                onRemoveExtra(activeExtra.photo_id);
              }
            }}
            data-testid="carousel-delete-current"
            aria-label={t("result.removePhotoAria")}
            title={t("result.removePhotoAria")}
            className="absolute top-3 right-3 w-7 h-7 rounded-full bg-black/45 backdrop-blur text-white/85 hover:bg-black/70 hover:text-white flex items-center justify-center transition z-10"
          >
            <TrashIcon className="w-3.5 h-3.5" strokeWidth={1.75} />
          </button>
        </>
      )}
    </>
  );
});

export default PhotoCarousel;
